import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, Circle, AlertTriangle, Trash2, ShieldCheck } from "lucide-react";
import { shortDate } from "@/lib/format";

const SHIPMENT_TYPES = [
  { value: "FCL_OCEAN", label: "Ocean FCL" },
  { value: "LCL_OCEAN", label: "Ocean LCL" },
  { value: "AIR", label: "Airfreight" },
];

export default function Documents() {
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState("");
  const [shipmentType, setShipmentType] = useState("FCL_OCEAN");
  const [checklist, setChecklist] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploadCode, setUploadCode] = useState("");

  // BL validate state
  const [bl, setBl] = useState({
    shipper: "", consignee: "", notify_party: "", vessel: "", port_of_loading: "",
    port_of_discharge: "", container_numbers: "", commodity_description: "",
    gross_weight_kg: 0, marks_and_numbers: "", freight_terms: "PREPAID", hs_code: "",
  });
  const [blResult, setBlResult] = useState(null);

  useEffect(() => {
    api.get("/shipments").then(r => {
      setShipments(r.data);
      if (r.data.length > 0) setSelectedShipment(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    api.get(`/documents/checklist/${shipmentType}`).then(r => setChecklist(r.data));
  }, [shipmentType]);

  useEffect(() => {
    if (selectedShipment) api.get(`/documents/shipment/${selectedShipment}`).then(r => setDocs(r.data));
  }, [selectedShipment]);

  const handleFile = async (e, code, label) => {
    const file = e.target.files?.[0];
    if (!file || !selectedShipment) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large — keep under 5MB for demo");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        await api.post("/documents", {
          shipment_id: selectedShipment,
          doc_code: code,
          label,
          file_name: file.name,
          mime_type: file.type,
          base64_data: base64,
        });
        toast.success(`${label} uploaded`);
        const r = await api.get(`/documents/shipment/${selectedShipment}`);
        setDocs(r.data);
      } catch {
        toast.error("Upload failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const deleteDoc = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Document removed");
    const r = await api.get(`/documents/shipment/${selectedShipment}`);
    setDocs(r.data);
  };

  const runBlValidate = async () => {
    const payload = {
      ...bl,
      shipment_id: selectedShipment || null,
      container_numbers: bl.container_numbers.split(",").map(c => c.trim()).filter(Boolean),
      gross_weight_kg: Number(bl.gross_weight_kg) || 0,
    };
    const r = await api.post("/documents/bl-validate", payload);
    setBlResult(r.data);
  };

  const completion = checklist.map(item => {
    const uploaded = docs.find(d => d.doc_code === item.code);
    return { ...item, uploaded };
  });
  const required = completion.filter(i => i.required);
  const requiredUploaded = required.filter(i => i.uploaded).length;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="documents-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Document Compliance Centre</h1>
        <p className="text-sm text-zinc-500 mt-1">Per-shipment checklist, document archive, and BL draft validation engine.</p>
      </div>

      <Tabs defaultValue="checklist">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 p-0 h-auto w-full justify-start">
          <TabsTrigger value="checklist" data-testid="tab-checklist" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">Checklist &amp; Upload</TabsTrigger>
          <TabsTrigger value="bl" data-testid="tab-bl-validate" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">BL Draft Validator</TabsTrigger>
        </TabsList>

        <TabsContent value="checklist" className="mt-6 space-y-6">
          <div className="bg-white border border-zinc-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="data-label">Shipment</Label>
              <Select value={selectedShipment} onValueChange={setSelectedShipment}>
                <SelectTrigger data-testid="doc-shipment-select" className="rounded-none mt-1.5"><SelectValue placeholder="Select shipment" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {shipments.map(s => <SelectItem key={s.id} value={s.id}>{s.id} · {s.origin_name} → {s.destination_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="data-label">Shipment Type</Label>
              <Select value={shipmentType} onValueChange={setShipmentType}>
                <SelectTrigger data-testid="doc-type-select" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {SHIPMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end">
              <Label className="data-label">Required Completion</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1 h-2 bg-zinc-100">
                  <div className="h-full bg-[#16A34A]" style={{ width: `${required.length ? (requiredUploaded / required.length) * 100 : 0}%` }} />
                </div>
                <span className="metric text-sm font-medium">{requiredUploaded}/{required.length}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-4 border-b border-zinc-200">
              <h3 className="font-heading text-lg font-bold tracking-tight">Document Checklist</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Upload &lt;5MB files. Marked items are mandatory.</p>
            </div>
            <div data-testid="checklist-items">
              {completion.map(item => (
                <div key={item.code} className="px-5 py-4 border-b border-zinc-100 flex items-center gap-4">
                  {item.uploaded ? <CheckCircle2 className="w-5 h-5 text-[#16A34A] flex-shrink-0" /> : <Circle className="w-5 h-5 text-zinc-300 flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {item.label}
                      {item.required && <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]">Required</span>}
                    </div>
                    {item.uploaded && (
                      <div className="text-xs text-zinc-500 mt-0.5 metric">{item.uploaded.file_name} · {Math.round(item.uploaded.size_bytes / 1024)} KB · {shortDate(item.uploaded.uploaded_at)}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.uploaded && (
                      <Button data-testid={`del-doc-${item.code}`} size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => deleteDoc(item.uploaded.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        data-testid={`upload-${item.code}`}
                        onChange={(e) => handleFile(e, item.code, item.label)}
                      />
                      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] px-3 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#E6EBF6]">
                        <UploadCloud className="w-3.5 h-3.5" />
                        {item.uploaded ? "Replace" : "Upload"}
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bl" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 p-6">
              <h3 className="font-heading text-lg font-bold tracking-tight mb-1">Draft Bill of Lading</h3>
              <p className="text-xs text-zinc-500 mb-4">Enter draft BL fields. Validator runs ISO 6346 container check, weight sanity, required field check.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["shipper", "Shipper", "text"],
                  ["consignee", "Consignee", "text"],
                  ["notify_party", "Notify Party", "text"],
                  ["vessel", "Vessel / Voyage", "text"],
                  ["port_of_loading", "Port of Loading", "text"],
                  ["port_of_discharge", "Port of Discharge", "text"],
                  ["container_numbers", "Container Numbers (csv)", "text"],
                  ["commodity_description", "Commodity Description", "text"],
                  ["gross_weight_kg", "Gross Weight (kg)", "number"],
                  ["marks_and_numbers", "Marks &amp; Numbers", "text"],
                  ["hs_code", "HS Code", "text"],
                ].map(([k, label, type]) => (
                  <div key={k} className={k === "shipper" || k === "consignee" || k === "container_numbers" || k === "commodity_description" ? "col-span-2" : ""}>
                    <Label className="data-label" dangerouslySetInnerHTML={{ __html: label }} />
                    <Input data-testid={`bl-${k}`} type={type} value={bl[k]} onChange={e => setBl({ ...bl, [k]: e.target.value })} className={`rounded-none mt-1 ${type === 'number' ? 'metric' : ''}`} />
                  </div>
                ))}
                <div>
                  <Label className="data-label">Freight Terms</Label>
                  <Select value={bl.freight_terms} onValueChange={v => setBl({ ...bl, freight_terms: v })}>
                    <SelectTrigger data-testid="bl-terms" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{["PREPAID","COLLECT"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="bl-validate-btn" onClick={runBlValidate} className="rounded-none bg-[#002FA7] hover:bg-[#002480] w-full mt-5"><ShieldCheck className="w-4 h-4 mr-2" />Validate Draft</Button>
            </div>

            <div className="bg-white border border-zinc-200 p-6" data-testid="bl-result">
              <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Validation Result</h3>
              {!blResult && <p className="text-sm text-zinc-500">Enter BL data and click Validate Draft.</p>}
              {blResult && (
                <>
                  <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-5">
                    <div className={`bg-white p-4 ${blResult.status === 'VALID' ? 'border-l-[3px] border-l-[#16A34A]' : blResult.status === 'REJECT' ? 'border-l-[3px] border-l-[#DC2626]' : 'border-l-[3px] border-l-[#F59E0B]'}`}>
                      <div className="data-label">Status</div>
                      <div className="metric text-2xl mt-1 font-bold" style={{ color: blResult.status === 'VALID' ? '#166534' : blResult.status === 'REJECT' ? '#991B1B' : '#B45309' }}>{blResult.status}</div>
                    </div>
                    <div className="bg-white p-4">
                      <div className="data-label">Score</div>
                      <div className="metric text-2xl mt-1">{blResult.score}<span className="text-zinc-400 text-base">/100</span></div>
                    </div>
                    <div className="bg-white p-4">
                      <div className="data-label">Findings</div>
                      <div className="metric text-2xl mt-1">{blResult.total_findings}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {blResult.findings.map((f, i) => (
                      <div key={i} className={`p-3 border ${f.severity === 'CRITICAL' ? 'bg-[#FEE2E2] border-[#FCA5A5]' : f.severity === 'WARNING' ? 'bg-[#FEF3C7] border-[#FCD34D]' : 'bg-zinc-50 border-zinc-200'}`}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${f.severity === 'CRITICAL' ? 'text-[#991B1B]' : f.severity === 'WARNING' ? 'text-[#B45309]' : 'text-zinc-500'}`} />
                          <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-600">{f.field} · {f.severity}</div>
                            <div className="text-sm mt-0.5">{f.message}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
